// Core
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetLossesYearsQuery } from "state/erse/lossesApiSlice";
import { useGetTariffsValiditiesQuery } from "state/erse/tariffsApiSlice";

function ERSERow({
  title,
  description,
  hrefTemplate,
  inputAccept,
  handleFileUpload: onUpload,
  isLoading,
  isDisabled,
  values,
  cardLabel,
  onViewClicked,
  onDeleteClicked,
}) {
  const { t } = useTranslation(["erse"]);

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-[2px]">
        <span className="text-[13.5px] font-semibold text-ink">{title}</span>
        {values?.length > 0 ? (
          values.map((value) => (
            <span key={value} className="rec-chip rec-chip-teal">
              <button
                type="button"
                className="bg-transparent border-0 p-0 cursor-pointer font-bold text-inherit"
                onClick={() => onViewClicked(value)}
              >
                {`${cardLabel} ${value}`}
              </button>
              {onDeleteClicked && (
                <button
                  type="button"
                  className="bg-transparent border-0 p-0 cursor-pointer text-inherit inline-flex"
                  title={t("list.card.confirm_remove", { defaultValue: "Remover" })}
                  onClick={() => onDeleteClicked(value)}
                >
                  <Icon name="close" style={{ fontSize: 13 }} />
                </button>
              )}
            </span>
          ))
        ) : (
          <span className="text-[12px] text-ink-3 italic">{t("list.empty")}</span>
        )}
        {hrefTemplate && (
          <a className="rec-btn-ghost rec-btn-sm no-underline" href={hrefTemplate}>
            <Icon name="download" style={{ fontSize: 16 }} />
            {t("list.buttons.template")}
          </a>
        )}
        {onUpload && !isLoading && <UploadHelpButton />}
        {onUpload && !isLoading && (
          <label
            htmlFor={`erse-upload-${cardLabel}`}
            className="rec-btn-ghost rec-btn-sm"
            style={isDisabled ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <Icon name="upload" style={{ fontSize: 16 }} />
            {t("list.buttons.upload")}
            <input
              id={`erse-upload-${cardLabel}`}
              type="file"
              accept={inputAccept}
              hidden
              onChange={onUpload}
            />
          </label>
        )}
        {isLoading && (
          <span className="rec-chip rec-chip-amber">
            <Icon name="hourglass_top" style={{ fontSize: 13 }} />
            {t("list.buttons.uploading", { defaultValue: "…" })}
          </span>
        )}
      </div>
      <div className="text-[12px] text-ink-3 leading-[1.5]">{description}</div>
    </div>
  );
}

ERSERow.defaultProps = {
  hrefTemplate: undefined,
  handleFileUpload: undefined,
  isLoading: false,
  isDisabled: false,
  onDeleteClicked: undefined,
  values: null,
};

ERSERow.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  hrefTemplate: PropTypes.string,
  inputAccept: PropTypes.string.isRequired,
  handleFileUpload: PropTypes.func,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  cardLabel: PropTypes.string.isRequired,
  onViewClicked: PropTypes.func.isRequired,
  onDeleteClicked: PropTypes.func,
};

function ERSEConfig() {
  const navigate = useNavigate();
  const { t } = useTranslation(["erse"]);
  const { t: tCommunity } = useTranslation(["community"]);

  const community = useSelector(selectCurrentCommunity);

  const { data: lossesYears } = useGetLossesYearsQuery();
  const { data: tariffsValidities } = useGetTariffsValiditiesQuery();

  const onViewClicked = (type) => (value) => {
    navigate(`/communities/${community.id}/erse/${type}/${value}`);
  };

  return (
    <SectionCard id="erse" icon="request_quote" title={tCommunity("sidenav.erse_tariffs")}>
      <div className="flex flex-col gap-4">
        <ERSERow
          title={t("list.sections.tariffs.title")}
          description={t("list.sections.tariffs.description")}
          inputAccept=".xlsx"
          values={tariffsValidities?.map((obj) => obj.validity)}
          cardLabel={t("list.card.label_valid_from")}
          onViewClicked={onViewClicked("tariffs")}
        />
        <ERSERow
          title={t("list.sections.losses.title")}
          description={t("list.sections.losses.description")}
          inputAccept=".csv"
          values={lossesYears?.map((obj) => obj.year)}
          cardLabel={t("list.card.label_year")}
          onViewClicked={onViewClicked("losses")}
        />
      </div>
    </SectionCard>
  );
}

export default ERSEConfig;
